import React, { useState, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  Step,
  Stepper,
  StepLabel,
  IconButton,
  TextField,
  Card,
  CardContent,
  LinearProgress,
  Chip,
} from '@mui/material';
import { Close, CloudUpload, CheckCircle, Error as ErrorIcon, CheckBox, CheckBoxOutlineBlank, IndeterminateCheckBox, Sync } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { DataGrid, GridSortModel } from '@mui/x-data-grid';
import { ptBR } from '@mui/x-data-grid/locales';
import EscritorioSelect from '../../selects/EscritorioSelect';
import { ixcImportService, IxcPlanoListItem, IxcImportPlanosResult } from '../../../services/ixcImportService';

const STORAGE_KEY_BASE_URL = 'sysup_ixc_import_baseUrl';
const STORAGE_KEY_TOKEN = 'sysup_ixc_import_token';

interface ImportIxcPlanosModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const steps = ['Configuração da API IXC', 'Selecionar planos', 'Resultado'];

export const ImportIxcPlanosModal: React.FC<ImportIxcPlanosModalProps> = ({ open, onClose, onSuccess }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [baseUrl, setBaseUrl] = useState('');
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [planos, setPlanos] = useState<IxcPlanoListItem[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [escritorioId, setEscritorioId] = useState<number | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<IxcImportPlanosResult | null>(null);
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'id', sort: 'asc' }]);

  useEffect(() => {
    if (open) {
      try {
        const savedUrl = localStorage.getItem(STORAGE_KEY_BASE_URL);
        const savedToken = localStorage.getItem(STORAGE_KEY_TOKEN);
        if (savedUrl) setBaseUrl(savedUrl);
        if (savedToken) setToken(savedToken);
      } catch {}
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    try {
      if (baseUrl.trim()) localStorage.setItem(STORAGE_KEY_BASE_URL, baseUrl.trim());
      if (token) localStorage.setItem(STORAGE_KEY_TOKEN, token);
    } catch {}
  }, [open, baseUrl, token]);

  const handleBuscarPlanos = async () => {
    if (!baseUrl.trim() || !token.trim()) {
      setError('Informe a URL base e a credencial da API IXC');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const list = await ixcImportService.listPlanos({ baseUrl: baseUrl.trim(), token: token.trim() });
      setPlanos(list);
      setSelectedIds(new Set());
      setActiveStep(1);
      toast.success(`${list.length} planos encontrados. Selecione os que deseja importar.`);
    } catch (err: any) {
      const msg = err?.message || 'Erro ao buscar planos na API IXC';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === planos.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(planos.map((p) => String(p.id))));
  };

  const rows = useMemo(() => planos.map((p) => ({ ...p, rowId: String(p.id) })), [planos]);
  const sortedRows = useMemo(() => {
    if (!sortModel.length) return rows;
    const [s] = sortModel;
    const field = s.field as keyof IxcPlanoListItem;
    const order = s.sort === 'desc' ? -1 : 1;
    return [...rows].sort((a, b) => {
      const va = a[field];
      const vb = b[field];
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * order;
      return String(va).localeCompare(String(vb)) * order;
    });
  }, [rows, sortModel]);

  const handleImportar = async () => {
    if (!escritorioId || selectedIds.size === 0) {
      setError('Selecione o escritório e ao menos um plano.');
      return;
    }
    setImporting(true);
    setError(null);
    try {
      const result = await ixcImportService.importarPlanos({
        baseUrl: baseUrl.trim(),
        token: token.trim(),
        idsPlanosIxc: Array.from(selectedIds),
        escritorio_id: escritorioId,
      });
      setImportResult(result);
      setActiveStep(2);
      if (result.success) toast.success(`${result.success} plano(s) importado(s).`);
      if (result.errors) toast.warning(`${result.errors} plano(s) com erro.`);
      if (result.errors === 0 && result.success > 0) onSuccess?.();
    } catch (err: any) {
      setError(err?.message || 'Erro na importação');
      toast.error(err?.message);
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setActiveStep(0);
    setPlanos([]);
    setSelectedIds(new Set());
    setEscritorioId(null);
    setError(null);
    setImporting(false);
    setImportResult(null);
    onClose();
  };

  const renderStep0 = () => (
    <Box>
      <Typography variant="body1" gutterBottom>
        Use a mesma URL e credencial (id:token) da importação de clientes. Os planos vêm do radgrupos (IXC).
      </Typography>
      <Card sx={{ mt: 2, p: 2 }}>
        <CardContent>
          <TextField
            label="URL base da API IXC"
            placeholder="https://seu-ixc.com.br"
            value={baseUrl}
            onChange={(e) => { setBaseUrl(e.target.value); setError(null); }}
            fullWidth
            sx={{ mb: 2 }}
            helperText="Ex: https://instancia.ixcsoft.com.br"
          />
          <TextField
            label="Credencial (id:token)"
            type="password"
            value={token}
            onChange={(e) => { setToken(e.target.value); setError(null); }}
            fullWidth
            sx={{ mb: 2 }}
            helperText="Formato: id do usuário : token da API"
          />
          <Button variant="contained" onClick={handleBuscarPlanos} disabled={loading} startIcon={loading ? <Sync className="animate-spin" /> : <CloudUpload />}>
            {loading ? 'Buscando...' : 'Buscar planos'}
          </Button>
        </CardContent>
      </Card>
    </Box>
  );

  const renderStep1 = () => (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Nome do plano no Sysup = campo &quot;grupo&quot; do IXC. O ID do plano no IXC será salvo em &quot;obs&quot;.
      </Typography>
      <Box sx={{ height: 400, width: '100%' }}>
        <DataGrid
          rows={sortedRows}
          getRowId={(row) => row.rowId}
          columns={[
            {
              field: 'checkbox',
              headerName: '',
              width: 50,
              sortable: false,
              renderHeader: () => (
                <IconButton size="small" onClick={toggleSelectAll}>
                  {selectedIds.size === 0 ? <CheckBoxOutlineBlank fontSize="small" /> : selectedIds.size === planos.length ? <CheckBox fontSize="small" color="primary" /> : <IndeterminateCheckBox fontSize="small" color="primary" />}
                </IconButton>
              ),
              renderCell: (params) => (
                <IconButton size="small" onClick={() => toggleSelect(params.row.rowId)}>
                  {selectedIds.has(params.row.rowId) ? <CheckBox fontSize="small" color="primary" /> : <CheckBoxOutlineBlank fontSize="small" />}
                </IconButton>
              ),
            },
            { field: 'id', headerName: 'ID', width: 80, sortable: true },
            { field: 'grupo', headerName: 'Grupo (nome do plano)', flex: 1, minWidth: 200, sortable: true },
            { field: 'download', headerName: 'Download', width: 120, sortable: true },
            { field: 'upload', headerName: 'Upload', width: 120, sortable: true },
            {
              field: 'valor_produto',
              headerName: 'Valor',
              width: 120,
              sortable: true,
              valueFormatter: (value: any) => {
                const val = parseFloat(String(value || 0));
                return isNaN(val) ? 'R$ 0,00' : val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
              }
            },
          ]}
          sortModel={sortModel}
          onSortModelChange={(model) => setSortModel(model)}
          localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          disableRowSelectionOnClick
          density="compact"
        />
      </Box>
      <Box sx={{ mt: 2 }}>
        <EscritorioSelect value={escritorioId} onChange={(v) => setEscritorioId(v as number | null)} label="Escritório" required helperText="Escritório onde os planos ficarão disponíveis" />
      </Box>
    </Box>
  );

  const renderStep2 = () =>
    importResult && (
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {importResult.success} importado(s), {importResult.errors} erro(s).
        </Typography>
        <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
          {(importResult.results || []).map((r, i) => (
            <Card key={i} sx={{ mb: 1 }}>
              <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                  <Typography variant="body2" fontWeight="medium">{r.nome || `ID ${r.id_ixc}`}</Typography>
                  {r.status === 'sucesso' ? <Chip icon={<CheckCircle />} label="Sucesso" color="success" size="small" /> : <Chip icon={<ErrorIcon />} label="Erro" color="error" size="small" />}
                </Box>
                {r.erro && <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>{r.erro}</Typography>}
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>
    );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Importar planos do IXC (radgrupos)</Typography>
          <IconButton onClick={handleClose} size="small"><Close /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}><StepLabel>{label}</StepLabel></Step>
          ))}
        </Stepper>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {loading && <LinearProgress sx={{ mb: 2 }} />}
        {activeStep === 0 && renderStep0()}
        {activeStep === 1 && renderStep1()}
        {activeStep === 2 && renderStep2()}
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } } .animate-spin { animation: spin 1s linear infinite; }`}</style>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancelar</Button>
        {activeStep === 0 && <Button variant="contained" onClick={handleBuscarPlanos} disabled={loading}>{loading ? 'Buscando...' : 'Buscar planos'}</Button>}
        {activeStep === 1 && (
          <>
            <Button onClick={() => setActiveStep(0)}>Voltar</Button>
            <Button variant="contained" onClick={handleImportar} disabled={selectedIds.size === 0 || !escritorioId || importing} startIcon={importing ? <Sync className="animate-spin" /> : <CloudUpload />}>
              {importing ? 'Importando...' : `Importar ${selectedIds.size} plano(s) selecionado(s)`}
            </Button>
          </>
        )}
        {activeStep === 2 && <Button variant="contained" onClick={handleClose}>Fechar</Button>}
      </DialogActions>
    </Dialog>
  );
};

export default ImportIxcPlanosModal;
